import {useBoardLabels} from '@github-ui/board-core'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'

import styles from './BoardSkeletons.module.css'

/** How many placeholder cards to draw when the caller states no page size. */
const FALLBACK_PAGE_SIZE = 3

export type BoardCardSkeletonProps = {
  className?: string
}

/**
 * One placeholder card.
 *
 * Carries a test id because it has no role or name to be found by: the bars are
 * decorative and the card is hidden from assistive technology, so a test that checks a
 * cell drew a page-shaped skeleton has nothing semantic to locate.
 */
export function BoardCardSkeleton({className}: BoardCardSkeletonProps) {
  return (
    <div className={clsx(styles.CardSkeleton, className)} data-testid="board-card-skeleton" aria-hidden>
      <LoadingSkeleton height="sm" width="random" variant="rounded" />
      <LoadingSkeleton height="sm" width="40%" variant="rounded" />
    </div>
  )
}

export type BoardCardSkeletonsProps = {
  /** `pageSize` from the board's data. A page arrives whole, so the skeleton is page-shaped. */
  count?: number
  className?: string
}

/**
 * A page of placeholder cards.
 *
 * As many placeholders as the caller asks for, so a page of cards that is loaded and
 * returned whole can be drawn as the transition from N placeholders to N cards.
 *
 * The board's own cell asks for one. A placeholder reserves the room its card lands in,
 * and a page-shaped reservation inside a cell leaves the reader exactly where they were
 * when the page arrives -- the scrollable area barely moves, so the board feels unchanged
 * by the thing it just did. A caller composing its own cell can still size this by
 * `pageSize`, which is what that field is for.
 *
 * The label is a real text node rather than an `aria-label` on the container. The bars
 * are decorative and hidden, so a container named by an attribute would carry a name
 * with no content behind it and be dropped from the reading order entirely.
 */
export function BoardCardSkeletons({count, className}: BoardCardSkeletonsProps) {
  const labels = useBoardLabels()
  const total = count ?? FALLBACK_PAGE_SIZE

  return (
    <div className={clsx(styles.CardSkeletons, className)}>
      <VisuallyHidden>{labels.loadingItems}</VisuallyHidden>
      {Array.from({length: total}, (_, index) => (
        <BoardCardSkeleton key={index} />
      ))}
    </div>
  )
}

export type BoardItemsLoadingRowProps = {
  className?: string
}

/**
 * One cell's "a page is on its way": a single card, drawn as a placeholder.
 *
 * One rather than a page of them, and shaped like a card rather than reduced to a bar. A
 * bar at the end of a list of cards says something is happening without saying what is
 * coming; a card says a card is coming, which is the honest description of the next thing
 * to arrive.
 *
 * **Exactly as tall as the room `BoardPaging.module.css` keeps for it**, which is what
 * lets it appear without moving the reader a pixel: a cell that has more holds a card's
 * worth of room open at the end of its cards at all times, so arriving here is a swap
 * rather than a growth, and it is already on screen for a reader who has reached the end.
 *
 * The two heights are a contract, and a silent one, because nothing in CSS can state it.
 * `shows the reader a page is on its way, without moving them to do it` asserts the cell's
 * extent does not change across the swap, so drifting them apart fails a test rather than
 * quietly restoring the fault this was built to fix.
 *
 * The label is a real text node rather than an `aria-label` on the container, for the
 * reason `BoardCardSkeletons` gives.
 */
export function BoardItemsLoadingRow({className}: BoardItemsLoadingRowProps) {
  const labels = useBoardLabels()

  return (
    // Carries a test id as well as its wording, because what has to be asserted about it
    // is the box: that it is as tall as the room the cell keeps for it, and that all of
    // that box is on screen. The wording finds the text node, which is not the box.
    <div className={clsx(styles.ItemsLoadingRow, className)} data-testid="board-items-loading">
      <VisuallyHidden>{labels.loadingItems}</VisuallyHidden>
      <BoardCardSkeleton />
    </div>
  )
}

export type BoardColumnSkeletonProps = {
  className?: string
}

/** A ghost column, drawn while a page of columns is in flight. */
export function BoardColumnSkeleton({className}: BoardColumnSkeletonProps) {
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.ColumnSkeleton, className)}>
      <VisuallyHidden>{labels.loadingColumns}</VisuallyHidden>
      <LoadingSkeleton height="md" variant="rounded" aria-hidden />
    </div>
  )
}

export type BoardRowSkeletonProps = {
  className?: string
}

/** A ghost row, drawn while a page of rows is in flight. */
export function BoardRowSkeleton({className}: BoardRowSkeletonProps) {
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.RowSkeleton, className)}>
      <VisuallyHidden>{labels.loadingRows}</VisuallyHidden>
      <LoadingSkeleton height="md" width="30%" variant="rounded" aria-hidden />
    </div>
  )
}
